import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Chip,
  Tabs,
  Tab,
  Input,
  Select,
  SelectItem,
  Skeleton,
} from '@nextui-org/react';
import {
  ArrowLeft,
  User as UserIcon,
  Trophy,
  Swords,
  CalendarDays,
  Flame,
  Target,
  TrendingUp,
  Search,
  AlertTriangle,
  BadgeCheck,
  Link2,
} from 'lucide-react';
import statsExplorerService, {
  StatsPlayerProfile,
  StatsPlayerMatch,
  MssPlayerData,
  MssMatch,
} from '../services/statsExplorerService';
import PlayerMatchRow, { WinLossBadge } from '../components/stats/PlayerMatchRow';
import StatCard from '../components/stats/StatCard';

const TEAL = '#3fced1';

// ============================================================
// PlayerExplorerPage – /statistiken/spieler/:playerId
// Der Spieler-Explorer: Header + Tabs
// (Übersicht | Ergebnisse | Kommende Spiele | MODUS Super Series)
// ============================================================

const PlayerExplorerPage: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();

  const [profile, setProfile] = useState<StatsPlayerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ergebnisse (paginiert, Seite 1 kommt aus dem Profil)
  const [results, setResults] = useState<StatsPlayerMatch[]>([]);
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsHasMore, setResultsHasMore] = useState(true);
  const [loadingMoreResults, setLoadingMoreResults] = useState(false);

  // Fixtures (paginiert)
  const [fixtures, setFixtures] = useState<StatsPlayerMatch[]>([]);
  const [fixturesPage, setFixturesPage] = useState(1);
  const [fixturesHasMore, setFixturesHasMore] = useState(true);
  const [loadingMoreFixtures, setLoadingMoreFixtures] = useState(false);

  // MSS
  const [mss, setMss] = useState<MssPlayerData | null>(null);

  const loadProfile = useCallback(async () => {
    if (!playerId) return;
    setLoading(true);
    setError(null);
    const data = await statsExplorerService.getPlayerProfile(playerId);
    if (!data || !data.player) {
      setError('Spieler konnte nicht geladen werden');
      setLoading(false);
      return;
    }
    setProfile(data);
    setResults(data.results || []);
    setResultsPage(1);
    setResultsHasMore((data.results || []).length > 0);
    setFixtures(data.fixtures || []);
    setFixturesPage(1);
    setFixturesHasMore((data.fixtures || []).length > 0);
    setLoading(false);
  }, [playerId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // MSS separat laden – Sektion wird bei Fehler/nicht verlinkt ausgeblendet
  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    statsExplorerService.getMssPlayerStats(playerId).then((data) => {
      if (!cancelled) setMss(data);
    });
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const loadMoreResults = useCallback(async () => {
    if (!playerId || loadingMoreResults || !resultsHasMore) return;
    setLoadingMoreResults(true);
    const nextPage = resultsPage + 1;
    const data = await statsExplorerService.getPlayerResults(playerId, nextPage);
    setResults((prev) => {
      const known = new Set(prev.map((m) => m.eventId).filter(Boolean));
      const fresh = data.matches.filter((m) => !m.eventId || !known.has(m.eventId));
      return [...prev, ...fresh];
    });
    setResultsPage(nextPage);
    setResultsHasMore(data.hasMore && data.matches.length > 0);
    setLoadingMoreResults(false);
  }, [playerId, resultsPage, resultsHasMore, loadingMoreResults]);

  const loadMoreFixtures = useCallback(async () => {
    if (!playerId || loadingMoreFixtures || !fixturesHasMore) return;
    setLoadingMoreFixtures(true);
    const nextPage = fixturesPage + 1;
    const data = await statsExplorerService.getPlayerFixtures(playerId, nextPage);
    setFixtures((prev) => {
      const known = new Set(prev.map((m) => m.eventId).filter(Boolean));
      const fresh = data.matches.filter((m) => !m.eventId || !known.has(m.eventId));
      return [...prev, ...fresh];
    });
    setFixturesPage(nextPage);
    setFixturesHasMore(data.hasMore && data.matches.length > 0);
    setLoadingMoreFixtures(false);
  }, [playerId, fixturesPage, fixturesHasMore, loadingMoreFixtures]);

  const player = profile?.player || null;
  const playerName = statsExplorerService.cleanPlayerName(player?.name || player?.shortName);
  const mssLinked = mss?.linked === true && mss.stats != null;

  // Win/Loss aus den geladenen Ergebnissen berechnen
  const recordStats = useMemo(() => {
    let wins = 0;
    let losses = 0;
    for (const match of results) {
      const won = statsExplorerService.didPlayerWin(match, player?.name, player?.shortName);
      if (won === true) wins++;
      else if (won === false) losses++;
    }
    const decided = wins + losses;
    return {
      total: results.length,
      wins,
      losses,
      winRate: decided > 0 ? Math.round((wins / decided) * 100) : null,
    };
  }, [results, player?.name, player?.shortName]);

  // ---------- Loading skeleton ----------
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Spieler-Statistiken | DartCloud</title>
        </Helmet>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Card className="bg-zinc-900 border border-zinc-800 mb-6">
            <CardBody className="p-6 flex flex-row items-center gap-5">
              <Skeleton className="rounded-full w-20 h-20 bg-zinc-800" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-6 w-2/5 rounded-lg bg-zinc-800" />
                <Skeleton className="h-4 w-1/4 rounded-lg bg-zinc-800" />
              </div>
            </CardBody>
          </Card>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl bg-zinc-900" />
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <Spinner size="lg" style={{ color: TEAL }} />
          </div>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !player) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Spieler nicht gefunden | DartCloud</title>
        </Helmet>
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <AlertTriangle size={56} className="mx-auto text-gray-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-300 mb-2">
            {error || 'Spieler nicht gefunden'}
          </h2>
          <p className="text-gray-500 mb-6">
            Der Spieler konnte nicht geladen werden. Bitte versuche es erneut.
          </p>
          <div className="flex justify-center gap-3">
            <Button
              onPress={loadProfile}
              style={{ backgroundColor: TEAL, color: '#000' }}
            >
              Erneut versuchen
            </Button>
            <Link to="/statistiken">
              <Button variant="flat" className="bg-zinc-800 text-white" startContent={<ArrowLeft size={14} />}>
                Zum Statistik-Explorer
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const flag = statsExplorerService.getFlagEmoji(player.countryName);

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>{playerName} – Statistiken & Ergebnisse | DartCloud</title>
        <meta
          name="description"
          content={`Alle Statistiken, historischen Ergebnisse und kommenden Spiele von ${playerName} im DartCloud Statistik-Explorer.`}
        />
        <link
          rel="canonical"
          href={`https://dartcloudapp.de/statistiken/spieler/${encodeURIComponent(playerId || '')}`}
        />
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link
          to="/statistiken"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeft size={14} /> Statistik-Explorer
        </Link>

        {/* Spieler-Header */}
        <Card className="bg-zinc-900 border border-zinc-800 mb-6">
          <CardBody className="p-5 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              {player.image ? (
                <img
                  src={player.image}
                  alt={playerName}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover bg-zinc-800 flex-shrink-0 border-2"
                  style={{ borderColor: `${TEAL}55` }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <UserIcon size={32} className="text-gray-500" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 flex-wrap">
                  {flag && <span>{flag}</span>}
                  {playerName}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {player.countryName && (
                    <Chip size="sm" variant="flat" className="bg-zinc-800 text-gray-300">
                      {player.countryName}
                    </Chip>
                  )}
                  {typeof player.ranking === 'number' && player.ranking > 0 && (
                    <Chip
                      size="sm"
                      variant="flat"
                      startContent={<Trophy size={12} />}
                      style={{ backgroundColor: `${TEAL}1f`, color: TEAL }}
                    >
                      Weltrangliste: {player.ranking}.
                    </Chip>
                  )}
                  {player.birthday && (
                    <Chip size="sm" variant="flat" className="bg-zinc-800 text-gray-300">
                      * {statsExplorerService.formatDate(player.birthday)}
                    </Chip>
                  )}
                </div>
              </div>

              {/* Win-Rate Donut */}
              {recordStats.winRate !== null && (
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: `conic-gradient(${TEAL} ${recordStats.winRate * 3.6}deg, #27272a 0deg)`,
                    }}
                  >
                    <div className="w-[60px] h-[60px] rounded-full bg-zinc-900 flex flex-col items-center justify-center">
                      <span className="text-base font-bold tabular-nums" style={{ color: TEAL }}>
                        {recordStats.winRate}%
                      </span>
                      <span className="text-[9px] uppercase tracking-wide text-gray-500">
                        Win-Rate
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    <p>
                      <span className="text-emerald-400 font-semibold">{recordStats.wins}</span> Siege
                    </p>
                    <p>
                      <span className="text-red-400 font-semibold">{recordStats.losses}</span> Niederlagen
                    </p>
                    <p className="text-gray-600">letzte {recordStats.total} Spiele</p>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Tabs */}
        <Tabs
          aria-label="Spieler-Statistik-Bereiche"
          variant="underlined"
          classNames={{
            tabList: 'gap-5 border-b border-zinc-800 w-full overflow-x-auto',
            cursor: 'bg-[#3fced1]',
            tab: 'px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-[#3fced1] text-gray-400',
          }}
        >
          <Tab
            key="uebersicht"
            title={
              <span className="flex items-center gap-2">
                <TrendingUp size={15} /> Übersicht
              </span>
            }
          >
            <OverviewTab
              recordStats={recordStats}
              results={results}
              playerName={player.name}
              playerShortName={player.shortName}
              mss={mssLinked ? mss : null}
            />
          </Tab>
          <Tab
            key="ergebnisse"
            title={
              <span className="flex items-center gap-2">
                <Swords size={15} /> Ergebnisse
              </span>
            }
          >
            <ResultsTab
              results={results}
              playerName={player.name}
              playerShortName={player.shortName}
              hasMore={resultsHasMore}
              loadingMore={loadingMoreResults}
              onLoadMore={loadMoreResults}
            />
          </Tab>
          <Tab
            key="fixtures"
            title={
              <span className="flex items-center gap-2">
                <CalendarDays size={15} /> Kommende Spiele
              </span>
            }
          >
            <FixturesTab
              fixtures={fixtures}
              playerName={player.name}
              playerShortName={player.shortName}
              hasMore={fixturesHasMore}
              loadingMore={loadingMoreFixtures}
              onLoadMore={loadMoreFixtures}
            />
          </Tab>
          {mssLinked ? (
            <Tab
              key="mss"
              title={
                <span className="flex items-center gap-2">
                  <Flame size={15} /> MODUS Super Series
                </span>
              }
            >
              <MssTab playerId={playerId || ''} mss={mss as MssPlayerData} />
            </Tab>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
};

// ============================================================
// Tab: Übersicht
// ============================================================

interface OverviewTabProps {
  recordStats: { total: number; wins: number; losses: number; winRate: number | null };
  results: StatsPlayerMatch[];
  playerName?: string | null;
  playerShortName?: string | null;
  mss: MssPlayerData | null;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  recordStats,
  results,
  playerName,
  playerShortName,
  mss,
}) => {
  // Form: letzte 5 Spiele (results sind absteigend sortiert)
  const form = useMemo(() => {
    const badges: (boolean | null)[] = [];
    for (const match of results) {
      if (badges.length >= 5) break;
      const won = statsExplorerService.didPlayerWin(match, playerName, playerShortName);
      if (won !== null) badges.push(won);
    }
    return badges;
  }, [results, playerName, playerShortName]);

  const mssStats = mss?.stats || null;

  return (
    <div className="pt-4 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Matches" value={recordStats.total} icon={<Swords size={14} />} sublabel="geladene Ergebnisse" />
        <StatCard label="Siege" value={recordStats.wins} icon={<Trophy size={14} />} />
        <StatCard label="Niederlagen" value={recordStats.losses} icon={<Target size={14} />} />
        <StatCard
          label="Win-Rate"
          value={recordStats.winRate !== null ? `${recordStats.winRate}%` : null}
          icon={<TrendingUp size={14} />}
          accent
        />
      </div>

      {/* Form-Indikator */}
      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
            Aktuelle Form (letzte 5 Spiele)
          </p>
          {form.length === 0 ? (
            <p className="text-sm text-gray-500">Keine ausgewerteten Spiele vorhanden</p>
          ) : (
            <div className="flex items-center gap-2">
              {form.map((won, idx) => (
                <WinLossBadge key={idx} won={won} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* MSS-Karten (nur wenn verlinkt) */}
      {mssStats && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} style={{ color: TEAL }} />
            <h3 className="text-sm font-semibold">MODUS Super Series</h3>
            <MssLinkBadge mss={mss} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Ø Average"
              value={typeof mssStats.avgAverage === 'number' ? mssStats.avgAverage.toFixed(2) : null}
              icon={<TrendingUp size={14} />}
              accent
            />
            <StatCard
              label="Bester Average"
              value={typeof mssStats.bestAverage === 'number' ? mssStats.bestAverage.toFixed(2) : null}
              icon={<TrendingUp size={14} />}
            />
            <StatCard label="180er" value={mssStats.total180s ?? null} icon={<Flame size={14} />} />
            <StatCard
              label="Höchster Checkout"
              value={mssStats.highestCheckout ?? null}
              icon={<Target size={14} />}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Tab: Ergebnisse (mit Filtern)
// ============================================================

interface ResultsTabProps {
  results: StatsPlayerMatch[];
  playerName?: string | null;
  playerShortName?: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

const ResultsTab: React.FC<ResultsTabProps> = ({
  results,
  playerName,
  playerShortName,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  const [textFilter, setTextFilter] = useState('');
  const [yearFilter, setYearFilter] = useState<string>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    for (const match of results) {
      const y = statsExplorerService.getYear(match.startTime);
      if (y) years.add(y);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [results]);

  const filtered = useMemo(() => {
    const text = textFilter.trim().toLowerCase();
    return results.filter((match) => {
      // Zeitraum (Jahr)
      if (yearFilter !== 'all') {
        const y = statsExplorerService.getYear(match.startTime);
        if (String(y) !== yearFilter) return false;
      }
      // Siege/Niederlagen
      if (outcomeFilter !== 'all') {
        const won = statsExplorerService.didPlayerWin(match, playerName, playerShortName);
        if (outcomeFilter === 'wins' && won !== true) return false;
        if (outcomeFilter === 'losses' && won !== false) return false;
      }
      // Textsuche: Gegner oder Turnier
      if (text) {
        const opponent = statsExplorerService.getOpponent(match, playerName, playerShortName);
        const haystack = [
          opponent?.name,
          match.home?.name,
          match.away?.name,
          match.tournamentName,
          match.round,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(text)) return false;
      }
      return true;
    });
  }, [results, textFilter, yearFilter, outcomeFilter, playerName, playerShortName]);

  return (
    <div className="pt-4">
      {/* Filter */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Input
          value={textFilter}
          onValueChange={setTextFilter}
          placeholder="Gegner oder Turnier suchen..."
          size="sm"
          startContent={<Search size={14} className="text-gray-500" />}
          classNames={{
            inputWrapper: 'bg-zinc-900 border border-zinc-800',
            input: 'text-white placeholder:text-gray-600',
          }}
          aria-label="Ergebnisse filtern"
        />
        <Select
          selectedKeys={[yearFilter]}
          onChange={(e) => setYearFilter(e.target.value || 'all')}
          size="sm"
          aria-label="Jahr filtern"
          classNames={{ trigger: 'bg-zinc-900 border border-zinc-800', listbox: 'bg-zinc-900' }}
        >
          {[
            <SelectItem key="all" value="all">
              Alle Jahre
            </SelectItem>,
            ...availableYears.map((y) => (
              <SelectItem key={String(y)} value={String(y)}>
                {String(y)}
              </SelectItem>
            )),
          ]}
        </Select>
        <Select
          selectedKeys={[outcomeFilter]}
          onChange={(e) => setOutcomeFilter(e.target.value || 'all')}
          size="sm"
          aria-label="Ergebnis filtern"
          classNames={{ trigger: 'bg-zinc-900 border border-zinc-800', listbox: 'bg-zinc-900' }}
        >
          <SelectItem key="all" value="all">
            Siege & Niederlagen
          </SelectItem>
          <SelectItem key="wins" value="wins">
            Nur Siege
          </SelectItem>
          <SelectItem key="losses" value="losses">
            Nur Niederlagen
          </SelectItem>
        </Select>
      </div>

      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center">
              <Swords size={36} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400 text-sm">
                {results.length === 0
                  ? 'Keine Ergebnisse vorhanden'
                  : 'Keine Ergebnisse für die gewählten Filter'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800/60">
              {filtered.map((match, idx) => (
                <PlayerMatchRow
                  key={match.eventId || idx}
                  match={match}
                  playerName={playerName}
                  playerShortName={playerShortName}
                />
              ))}
            </ul>
          )}

          {hasMore && (
            <div className="p-4 border-t border-zinc-800 flex justify-center">
              <Button
                onPress={onLoadMore}
                isLoading={loadingMore}
                isDisabled={loadingMore}
                variant="flat"
                className="bg-zinc-800 text-white"
              >
                {loadingMore ? 'Lädt...' : 'Mehr laden'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
      {(textFilter || yearFilter !== 'all' || outcomeFilter !== 'all') && hasMore && (
        <p className="text-xs text-gray-600 mt-2 text-center">
          Hinweis: Filter wirken auf die bereits geladenen Ergebnisse. Lade mehr, um ältere Spiele einzubeziehen.
        </p>
      )}
    </div>
  );
};

// ============================================================
// Tab: Kommende Spiele
// ============================================================

interface FixturesTabProps {
  fixtures: StatsPlayerMatch[];
  playerName?: string | null;
  playerShortName?: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

const FixturesTab: React.FC<FixturesTabProps> = ({
  fixtures,
  playerName,
  playerShortName,
  hasMore,
  loadingMore,
  onLoadMore,
}) => {
  return (
    <div className="pt-4">
      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-0">
          {fixtures.length === 0 ? (
            <div className="p-10 text-center">
              <CalendarDays size={36} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400 text-sm">Keine kommenden Spiele bekannt</p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800/60">
              {fixtures.map((match, idx) => (
                <PlayerMatchRow
                  key={match.eventId || idx}
                  match={match}
                  playerName={playerName}
                  playerShortName={playerShortName}
                  isFixture
                />
              ))}
            </ul>
          )}
          {hasMore && fixtures.length > 0 && (
            <div className="p-4 border-t border-zinc-800 flex justify-center">
              <Button
                onPress={onLoadMore}
                isLoading={loadingMore}
                isDisabled={loadingMore}
                variant="flat"
                className="bg-zinc-800 text-white"
              >
                {loadingMore ? 'Lädt...' : 'Mehr laden'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

// ============================================================
// Tab: MODUS Super Series
// ============================================================

const MssLinkBadge: React.FC<{ mss: MssPlayerData | null }> = ({ mss }) => {
  const link = mss?.link;
  if (!link?.externalName) return null;
  const verified = link.verified === true;
  return (
    <Chip
      size="sm"
      variant="flat"
      startContent={verified ? <BadgeCheck size={12} /> : <Link2 size={12} />}
      style={{ backgroundColor: `${TEAL}1f`, color: TEAL }}
      title={verified ? 'Manuell verifizierte Verknüpfung' : 'Automatisch verknüpft'}
    >
      Verknüpft mit „{link.externalName}“ ({verified ? 'verifiziert' : 'automatisch'})
    </Chip>
  );
};

interface MssTabProps {
  playerId: string;
  mss: MssPlayerData;
}

const MssTab: React.FC<MssTabProps> = ({ playerId, mss }) => {
  const [matches, setMatches] = useState<MssMatch[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const data = await statsExplorerService.getMssPlayerMatches(playerId, 1, 20);
      if (cancelled) return;
      setMatches(data.matches);
      setPage(1);
      setTotalPages(data.totalPages);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const loadMore = useCallback(async () => {
    if (loadingMore || page >= totalPages) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    const data = await statsExplorerService.getMssPlayerMatches(playerId, nextPage, 20);
    setMatches((prev) => [...prev, ...data.matches]);
    setPage(nextPage);
    if (data.totalPages > 0) setTotalPages(data.totalPages);
    setLoadingMore(false);
  }, [playerId, page, totalPages, loadingMore]);

  const stats = mss.stats || {};
  const perSeries = Array.isArray(stats.perSeries) ? stats.perSeries.filter((s) => s != null) : [];
  const winRate =
    typeof stats.winRate === 'number'
      ? `${Math.round(stats.winRate <= 1 ? stats.winRate * 100 : stats.winRate)}%`
      : null;

  return (
    <div className="pt-4 space-y-6">
      <MssLinkBadge mss={mss} />

      {/* Aggregierte Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Matches" value={stats.matches ?? null} icon={<Swords size={14} />} />
        <StatCard
          label="Bilanz"
          value={
            stats.wins != null || stats.losses != null
              ? `${stats.wins ?? 0} – ${stats.losses ?? 0}`
              : null
          }
          icon={<Trophy size={14} />}
          sublabel={winRate ? `Win-Rate: ${winRate}` : null}
        />
        <StatCard
          label="Ø Average"
          value={typeof stats.avgAverage === 'number' ? stats.avgAverage.toFixed(2) : null}
          icon={<TrendingUp size={14} />}
          accent
        />
        <StatCard
          label="Checkouts 100+"
          value={stats.checkouts100plus ?? null}
          icon={<Target size={14} />}
        />
      </div>

      {/* Pro Serie */}
      {perSeries.length > 0 && (
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-0">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Flame size={15} style={{ color: TEAL }} /> Statistiken pro Serie
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 border-b border-zinc-800">
                    <th className="px-4 py-2 font-semibold">Serie</th>
                    <th className="px-4 py-2 font-semibold text-right">Matches</th>
                    <th className="px-4 py-2 font-semibold text-right">Siege</th>
                    <th className="px-4 py-2 font-semibold text-right">Ø Average</th>
                    <th className="px-4 py-2 font-semibold text-right">180er</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {perSeries.map((s, idx) => (
                    <tr key={`${s.seriesName}-${idx}`}>
                      <td className="px-4 py-2.5 text-gray-200">{s.seriesName || '-'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                        {s.matches ?? '-'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                        {s.wins ?? '-'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: TEAL }}>
                        {typeof s.avgAverage === 'number' ? s.avgAverage.toFixed(2) : '-'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-300">
                        {s.total180s ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Match-Historie */}
      <Card className="bg-zinc-900 border border-zinc-800">
        <CardBody className="p-0">
          <div className="px-4 py-3 border-b border-zinc-800">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Swords size={15} style={{ color: TEAL }} /> Match-Historie
            </h3>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Spinner size="lg" style={{ color: TEAL }} />
            </div>
          ) : matches.length === 0 ? (
            <p className="px-4 py-8 text-sm text-gray-500 text-center">
              Keine Matches verfügbar
            </p>
          ) : (
            <ul className="divide-y divide-zinc-800/60">
              {matches.map((match, idx) => (
                <li
                  key={match.gameId || idx}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-zinc-800/30 transition-colors"
                >
                  <WinLossBadge won={match.won === true ? true : match.won === false ? false : null} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      vs. {match.opponentName || 'Unbekannt'}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500 mt-0.5">
                      <span>{statsExplorerService.formatDate(match.startTime)}</span>
                      {match.seriesName && (
                        <span style={{ color: `${TEAL}cc` }}>{match.seriesName}</span>
                      )}
                      {match.groupName && <span>{match.groupName}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-[11px] text-gray-500 mt-0.5 tabular-nums">
                      {typeof match.playerStats?.average === 'number' && (
                        <span>Avg: {match.playerStats.average.toFixed(2)}</span>
                      )}
                      {match.playerStats?.turns180 != null && (
                        <span>180er: {match.playerStats.turns180}</span>
                      )}
                      {match.playerStats?.highestCheckout != null && (
                        <span>Höchster Checkout: {match.playerStats.highestCheckout}</span>
                      )}
                    </div>
                  </div>
                  {match.playerLegs != null && match.opponentLegs != null && (
                    <span className="text-base font-bold tabular-nums flex-shrink-0">
                      {match.playerLegs}:{match.opponentLegs}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!loading && page < totalPages && (
            <div className="p-4 border-t border-zinc-800 flex justify-center">
              <Button
                onPress={loadMore}
                isLoading={loadingMore}
                isDisabled={loadingMore}
                variant="flat"
                className="bg-zinc-800 text-white"
              >
                {loadingMore ? 'Lädt...' : 'Mehr laden'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default PlayerExplorerPage;
